import { PHeading } from "@porsche-design-system/components-react"
import type { ActionItem, ListingScoreHintsView, VisibilityView } from "@slfinrtl/ui-bff-routes"
import { getListingScoreHintsViewRoute } from "@slfinrtl/ui-bff-routes"
import { useCallback, useEffect, useState } from "react"
import { FormattedMessage } from "react-intl"
import { useLocation } from "react-router"
import { Stage } from "src/components"
import { getVehicleDetailsAnalyticsPage, trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useBff, useSessionStorage } from "src/hooks"
import type { VehicleDetailsStage } from "../../common/VehicleDetailsStage"
import { useVehicleDetailsContext } from "../../context/VehicleDetailsContext"
import { ListingScoreHints } from "./components/ListingScoreHints"
import { ListingScoreHintsSkeleton } from "./components/ListingScoreHintsSkeleton"
import { listingScoreHintsMessages } from "./components/listingScoreHints.messages"
import { listingScoreSessionStorageKey } from "./listingScoreSessionStorageKey"

interface ListingScoreHintsContainerProps {
	imageEditView?: ActionItem
	onEditStageClick: (id: VehicleDetailsStage) => void
}

export const ListingScoreHintsContainer = withErrorHandler(
	({ imageEditView, onEditStageClick }: ListingScoreHintsContainerProps) => {
		const { inventoryId, subscribe } = useVehicleDetailsContext()
		const { pathname } = useLocation()
		const [listingScoreHintsView, setListingScoreHintsView] = useState<ListingScoreHintsView | null>()
		const [conditionAndServiceEdit, setConditionAndServiceEdit] = useState<ActionItem>()
		const [porscheApprovedEditView, setPorscheApprovedEditView] = useState<ActionItem>()
		const [visibilityView, setVisibilityView] = useState<VisibilityView>()
		const [expandedEntry, setExpandedEntry] = useSessionStorage(listingScoreSessionStorageKey, "false")

		const getListingScoreHints = useBff(
			getListingScoreHintsViewRoute,
			{
				body: undefined,
				pathParams: { inventoryId },
				queryParams: {},
			},
			"inline",
		)

		const fetchView = useCallback(
			() =>
				getListingScoreHints()
					.then(setListingScoreHintsView)
					.catch(() => setListingScoreHintsView(null)),
			[getListingScoreHints],
		)

		useEffect(() => {
			subscribe("inventoryInformationViewChanged", (view) => {
				setConditionAndServiceEdit(view?.conditionAndService.historyEdit)
			})
			subscribe("inventoryInformationUpdated", fetchView)
			subscribe("porscheApprovedEditActionChanged", setPorscheApprovedEditView)
			subscribe("visibilityChanged", setVisibilityView)
			subscribe("visibilityUpdated", fetchView)
		}, [subscribe, fetchView])

		useEffect(() => {
			fetchView()
		}, [fetchView])

		const handleExpandedChange = (expanded: boolean) => {
			setExpandedEntry(`${expanded}`)
			if (expanded) {
				trackEvent({
					action: "show_suggestions_listing_performance",
					category: getVehicleDetailsAnalyticsPage(pathname, "overview"),
				})
			}
		}

		return (
			<Stage>
				{listingScoreHintsView ? (
					<ListingScoreHints
						view={listingScoreHintsView}
						isExpanded={expandedEntry === "true"}
						onEditClick={onEditStageClick}
						onExpandedChange={handleExpandedChange}
						imageEditView={imageEditView}
						conditionAndServiceEditView={conditionAndServiceEdit}
						porscheApprovedEditView={porscheApprovedEditView}
						visibilityEditView={visibilityView?.editAction}
					/>
				) : listingScoreHintsView === undefined ? (
					<ListingScoreHintsSkeleton />
				) : listingScoreHintsView === null ? (
					<>
						<PHeading size="lg" tag="h2">
							<FormattedMessage {...listingScoreHintsMessages.headline} />
						</PHeading>
						<ErrorNotificationInline dismissButton={false} />
					</>
				) : null}
			</Stage>
		)
	},
)
